import { CargoOperation, CargoOperationStatus } from './_models'
import { OP } from './operationPermissions'
import { hasPermission } from '../../auth/core/permissionUtils'

export const isCompanyAdminUser = (currentUser: any): boolean => {
  if (!currentUser) return false
  const roleType = String(
    currentUser?.role?.roleType ??
      currentUser?.role?.effectiveBehaviorType ??
      currentUser?.tenant?.effectiveBehaviorType ??
      currentUser?.role?.roleName ??
      currentUser?.roleName ??
      ''
  ).toUpperCase()

  const roleId = Number(currentUser?.role?.id ?? 0)
  return (
    roleId === 1 ||
    roleId === 2 ||
    roleType.includes('SUPER_ADMIN') ||
    roleType.includes('COMPANY_GROUP_ADMIN') ||
    roleType.includes('COMPANY_ADMIN') ||
    roleType === 'COMPANY ADMIN' ||
    roleType === 'COMPANY GROUP ADMIN'
  )
}

export const isCrewUser = (currentUser: any): boolean => {
  if (!currentUser) return false
  const roleType = String(
    currentUser?.role?.roleType ??
      currentUser?.role?.effectiveBehaviorType ??
      currentUser?.tenant?.effectiveBehaviorType ??
      currentUser?.role?.roleName ??
      currentUser?.roleName ??
      ''
  ).toUpperCase()

  const roleId = Number(currentUser?.role?.id ?? 0)
  return roleId === 4 || roleType.includes('CREW')
}

export const isCrewMasterUser = (currentUser: any): boolean => {
  if (!currentUser) return false
  if (!isCrewUser(currentUser)) return false

  const rankId = Number(
    currentUser?.rank?.id ??
      currentUser?.rankId ??
      currentUser?.crew?.rank?.id ??
      0
  )
  const rankName = String(
    currentUser?.rank?.rank ??
      currentUser?.rankName ??
      currentUser?.crew?.rank?.rank ??
      currentUser?.crew?.rank ??
      currentUser?.rank ??
      ''
  ).toUpperCase()

  return (
    rankId === 1 || rankName.includes('MASTER') || rankName.includes('CAPTAIN')
  )
}

export const hasUserApprovalPermission = (currentUser: any): boolean => {
  if (!currentUser) return false
  if (isCompanyAdminUser(currentUser)) return true

  // Check resolvedPermissions map/set/array
  if (
    currentUser?.resolvedPermissions &&
    hasPermission(currentUser.resolvedPermissions, OP.APPROVE_CARGO_OPERATION)
  ) {
    return true
  }

  // Check permissions array or set
  if (
    currentUser?.permissions &&
    hasPermission(currentUser.permissions, OP.APPROVE_CARGO_OPERATION)
  ) {
    return true
  }

  // Check role?.permissions array or set
  if (
    currentUser?.role?.permissions &&
    hasPermission(currentUser.role.permissions, OP.APPROVE_CARGO_OPERATION)
  ) {
    return true
  }

  // Check authorities
  if (Array.isArray(currentUser?.authorities)) {
    if (
      currentUser.authorities.some(
        (a: any) =>
          a?.authority === OP.APPROVE_CARGO_OPERATION ||
          a?.authority === `ROLE_${OP.APPROVE_CARGO_OPERATION}`
      )
    ) {
      return true
    }
  }

  return false
}

export const canUserApproveOrReject = (
  currentUser: any,
  cargo?: CargoOperation | null,
  hasApprovalPermission?: boolean
): boolean => {
  // CRITICAL FIX: The approval button must ONLY be visible when the report is in PENDING_APPROVAL status.
  // Once approved (or if draft/rejected), nobody can see the approval button.
  if (!cargo || cargo.status !== 'PENDING_APPROVAL') {
    return false
  }

  if (!currentUser) return false

  const isCompanyAdmin = isCompanyAdminUser(currentUser)
  const isMaster = isCrewMasterUser(currentUser)
  const hasPerm =
    hasApprovalPermission === true || hasUserApprovalPermission(currentUser)

  // If user is a crew user, enforce vessel matching
  if (isCrewUser(currentUser)) {
    const userVesselId = Number(
      currentUser?.vessel?.id ??
        currentUser?.vesselId ??
        currentUser?.crew?.vessel_id?.id ??
        currentUser?.crew?.vesselId ??
        0
    )
    const cargoVesselId = Number(cargo.vesselId ?? 0)
    const isSameVessel = userVesselId > 0 && userVesselId === cargoVesselId

    // Crew Master or Crew with APPROVE_CARGO_OPERATION can approve their own vessel's reports
    return (isMaster || hasPerm) && isSameVessel
  }

  // Shore / Office roles: allowed if Company Admin or granted APPROVE_CARGO_OPERATION permission
  return isCompanyAdmin || hasPerm
}

export const isCargoReportLocked = (
  cargoOrStatus?: CargoOperation | CargoOperationStatus | null
): boolean => {
  if (!cargoOrStatus) return false
  if (typeof cargoOrStatus === 'string') {
    return cargoOrStatus === 'APPROVED'
  }
  return cargoOrStatus.status === 'APPROVED'
}

export const canUserEditCargo = (
  currentUser: any,
  cargo?: CargoOperation | null,
  hasApprovalPermission?: boolean
): boolean => {
  if (!cargo) return true
  if (isCargoReportLocked(cargo)) return false
  if (cargo.status === 'PENDING_APPROVAL') {
    // Only approvers can edit while pending, regular crew must wait
    return canUserApproveOrReject(currentUser, cargo, hasApprovalPermission)
  }
  return true
}

export const canUserDeleteCargo = (
  currentUser: any,
  cargo?: CargoOperation | null
): boolean => {
  if (!cargo) return false
  if (isCargoReportLocked(cargo)) return false
  if (cargo.status === 'PENDING_APPROVAL') return false
  return true
}

export const canUserSubmitCargo = (
  first: any,
  second?: any
): boolean => {
  const cargo = second !== undefined ? second : first
  if (!cargo) return false
  return cargo.status === 'DRAFT' || cargo.status === 'REJECTED'
}

export const getCargoStatusBadgeClass = (status?: CargoOperationStatus): string => {
  switch (status) {
    case 'APPROVED':
      return 'badge-light-success text-success'
    case 'PENDING_APPROVAL':
      return 'badge-light-warning text-warning'
    case 'REJECTED':
      return 'badge-light-danger text-danger'
    case 'DRAFT':
    default:
      return 'badge-light-secondary text-secondary'
  }
}

export const getCargoStatusLabel = (status?: CargoOperationStatus): string => {
  switch (status) {
    case 'APPROVED':
      return 'Approved'
    case 'PENDING_APPROVAL':
      return 'Pending Approval'
    case 'REJECTED':
      return 'Rejected'
    case 'DRAFT':
    default:
      return 'Draft'
  }
}
